import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Loader2,
  Edit,
  Mail,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const EmailTemplatesTab = ({ api, emailTemplates, fetchData }) => {
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [creating, setCreating] = useState(false);

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setCreating(true);
    try {
      await api().patch(`/admin/email-templates/${editingTemplate.id}`, editingTemplate);
      toast.success('Template saved');
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setCreating(false);
    }
  };

  const handlePreviewTemplate = async () => {
    if (!editingTemplate) return;
    try {
      const response = await api().post(`/admin/email-templates/${editingTemplate.id}/preview`, {});
      setTemplatePreview(response.data);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const insertTemplateVariable = (variable) => {
    if (!editingTemplate) return;
    const textarea = document.getElementById('template-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editingTemplate.body_html || '';
      const newText = text.substring(0, start) + `{{${variable}}}` + text.substring(end);
      setEditingTemplate({ ...editingTemplate, body_html: newText });
    } else {
      setEditingTemplate({
        ...editingTemplate,
        body_html: (editingTemplate.body_html || '') + `{{${variable}}}`
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Email Templates</h2>
          <p className="text-sm text-zinc-500">Customize automated email content</p>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2">
        {emailTemplates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-900 capitalize">
                      {template.name.replace(/_/g, ' ')}
                    </h3>
                    <p className="text-sm text-zinc-500 truncate max-w-[200px]">
                      {template.subject}
                    </p>
                  </div>
                </div>
                <Badge className={template.is_active ? 'bg-green-50 text-green-700' : 'bg-zinc-100 text-zinc-600'}>
                  {template.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setEditingTemplate(template);
                    setTemplatePreview(null);
                    setShowTemplateDialog(true);
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await api().post(`/admin/email-templates/${template.id}/preview`, {});
                      setTemplatePreview(response.data);
                      setEditingTemplate(template);
                      setShowTemplateDialog(true);
                    } catch (e) {
                      toast.error('Failed to preview');
                    }
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {emailTemplates.length === 0 && (
          <div className="col-span-2 p-8 text-center text-zinc-500 bg-zinc-50 rounded-lg">
            <Mail className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p>No email templates configured</p>
          </div>
        )}
      </div>

      {/* Email Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={(open) => {
        setShowTemplateDialog(open);
        if (!open) {
          setEditingTemplate(null);
          setTemplatePreview(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Edit Email Template
            </DialogTitle>
            <DialogDescription>
              Use variables like {'{{client_name}}'}, {'{{booking_date}}'} in your template
            </DialogDescription>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="template-subject">Subject Line</Label>
                <Input
                  id="template-subject"
                  value={editingTemplate.subject || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  className="mt-2"
                  data-testid="template-subject"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="template-body">Email Body (HTML)</Label>
                  <Select onValueChange={insertTemplateVariable}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Insert variable" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client_name">Client Name</SelectItem>
                      <SelectItem value="booking_date">Booking Date</SelectItem>
                      <SelectItem value="booking_time">Booking Time</SelectItem>
                      <SelectItem value="service_address">Service Address</SelectItem>
                      <SelectItem value="staff_name">Staff Name</SelectItem>
                      <SelectItem value="company_name">Company Name</SelectItem>
                      <SelectItem value="user_name">User Name</SelectItem>
                      <SelectItem value="set_password_link">Set Password Link</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <textarea
                  id="template-body"
                  value={editingTemplate.body_html || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, body_html: e.target.value })}
                  className="w-full h-48 px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono"
                  data-testid="template-body"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="template-active"
                  checked={editingTemplate.is_active}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="template-active">Template is active</Label>
              </div>
              
              {/* Preview Section */}
              {templatePreview && (
                <div className="border rounded-lg p-4 bg-zinc-50">
                  <h4 className="font-medium text-sm text-zinc-700 mb-2">Preview</h4>
                  <div className="bg-white rounded border p-4">
                    <p className="font-medium text-zinc-900 mb-2">Subject: {templatePreview.subject}</p>
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: templatePreview.body_html }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handlePreviewTemplate} className="w-full sm:w-auto">
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveTemplate} disabled={creating} data-testid="save-template-btn">
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Template
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesTab;
